import type { AxiosInstance } from "axios";
import { DATA_URL } from "~/env";
import type { FetchEventSourceConfig } from "~/utils/axios-utils";
import { sendRequest, sendSSERequest } from "~/utils/axios-utils";

const treeApiList = {
  case: "/api/v1/case/treeList",
  laws: "/api/v1/laws/treeList",
  relatedLaws: "/api/v2/laws/relatedTreeList",
  viewpoint: "/api/v1/viewpoint/treeList",
  lawNews: "/api/v1/lawNews/treeList",
  ap: "/api/v1/ap/tree_list",
  pd: "/api/v1/pd/tree_list",
  judge: "/api/v1/judge/treeList",
  lawyer: "/api/v1/lawyer/treeList",
  lawFirm: "/api/v1/lawFirm/treeList",
  contract: "/api/v1/contract/treeList",
  patent: "/api/v1/patent/treeList",
  trademark: "/api/v1/trademark/treeList",
};

const resultApiList = {
  case: "/api/v1/case/queryList",
  laws: "/api/v2/laws/queryList",
  relatedLaws: "/api/v2/laws/relatedQueryList",
  moreLaws: "/api/v2/laws/queryGroupItems",
  viewpoint: "/api/v1/viewpoint/queryList",
  jianwei: "/api/v1/jianwei/viewpoint/queryList",
  lawNews: "/api/v1/lawNews/queryList",
  ap: "/api/v1/ap/query_list",
  pd: "/api/v1/pd/query_list",
  judge: "/api/v1/judge/queryList",
  lawyer: "/api/v1/lawyer/queryList",
  lawFirm: "/api/v1/lawFirm/queryList",
  contract: "/api/v1/contract/queryList",
  patent: "/api/v1/patent/queryList",
  trademark: "/api/v1/trademark/queryList",
};

export default ($axios: AxiosInstance) => {
  $axios.defaults.baseURL = DATA_URL;

  return {
    /**
     * 获取跳转到检索结果页的URL
     */
    getLegalResultSearchUrl: (id: string, type: string = "case") =>
      `${DATA_URL}/list?functionType=keyword&type=${type}&searchKey=${id}`,
    /**
     * 获取跳转案例详情的URL
     */
    getLegalCaseDetailUrl: (id: string, queryId: string = "") =>
      `${DATA_URL}/caseDetail/${id}?queryId=${queryId}&type=case`,
    /**
     * 获取跳转法规详情的URL
     */
    getLegalLawsDetailUrl: (id: string, queryId: string = "") =>
      `${DATA_URL}/lawsDetail/${id}?queryId=${queryId}&type=laws`,
    /**
     * 获取法问url
     */
    getLegalLawQuestionUrl: () => `${DATA_URL}/lawQuestion`,
    /**
     * 将文本转换为MD5
     */
    toMD5: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v1/md5/search_md5",
        data: {
          text: JSON.stringify(params),
        },
      }),
    /**
     * 将MD5转换为文本
     */
    parseMD5: (md5Text: string) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v1/md5/search_text",
        data: { md5: md5Text },
      }).then((res) => (res !== null ? JSON.parse(res) : null)),
    /**
     * 检索栏目树
     */
    getTreeList: (searchType: SearchType, params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: treeApiList[searchType],
        data: params,
      }),
    /**
     * 检索结果
     */
    getResultList: (searchType: SearchType, params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: resultApiList[searchType],
        data: params,
      }),
    /**
     * AI问答
     */
    questionsAndAnswers: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v2/gpt/question/questionsAndAnswers",
        data: params,
      }),
    /**
     *查询tab统计数量
     */
    searchTabCount: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v1/tab_num/search_tab_count",
        data: params,
      }),
    /**
     * 查询法规/法条
     */
    quoteLawName: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: "/api/v1/case/quoteLawName",
        params,
      }),
    /**
     * 查询智能摘要，裁判观点
     */
    smartSummaryGenerateStream: (params: any, config?: FetchEventSourceConfig) =>
      sendSSERequest(`${DATA_URL}/api/v1/case/smartSummaryGenerateStream`, "POST", params, config, true),

    /**
     * 查询智能摘要，裁判观点
     */
    smartSummaryGenerateV2: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v1/case/smartSummaryGenerateV2",
        data: params,
      }),
    /**
     * 获取深i企付费套餐/api/v1/pay/getPlanList
     */
    getPlanList: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: `${DATA_URL}/api/v2/pay/getPlanList`,
        params,
      }),

    /**
     * 获取法搜小程序jssdk后台签名接口
     */
    getWXConfig: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: "/api/v1/user/getWXConfig",
        params,
      }),

    /**
     * h5获取案例详情
     */
    getCaseDetail: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: "/api/v1/case/detail",
        params,
      }),
    /**
     * h5获取法规详情
     */
    getLawsDetail: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: "/api/v1/laws/detail",
        params,
      }),
    /**
     * 计算套餐费用
     */
    computeSetMealFee: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v2/pay/computeSetMealFee",
        data: params,
      }),
    // 生成订单号
    buySetMeal: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v2/pay/buySetMeal",
        data: params,
      }),
    // 生成用户标识
    getSign: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v1/pay/getSign",
        data: params,
      }),
    // 获取订单详情
    getPayOrderDetail: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: "/api/v2/pay/getOrderInfo",
        params,
      }),
    //获取编辑文件
    getEdit: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: "/api/v1/case/getFileEditor",
        params,
      }),
    wpsTokenRefresh: (token: any) =>
      sendRequest($axios, {
        method: "GET",
        url: `/api/v1/file/refreshToken/${token}`,
      }),
    // 获取文件预览地址
    getViewUrlWebPath: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: `${DATA_URL}/api/file/getViewUrlWebPath`,
        params,
      }),
    getCustomerAnswer: (params: any, config?: FetchEventSourceConfig) =>
      sendSSERequest(`${DATA_URL}/api/v1/ai/customerService/getAnswer`, "POST", params, config, true),
    // 获取会员介绍图片
    getVipImageUrl: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: `${DATA_URL}/api/v1/home/getDataList`,
        params,
      }),
    switchVersion: (params: { versionType: number }) =>
      sendRequest($axios, {
        method: "POST",
        url: `${DATA_URL}/api/v1/enterprise/switchVersion`,
        data: params,
      }),
  };
};
